import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import defaultLabels, { Labels } from './labels';
import { fetchWithAuth } from './api';

const LABELS_STORAGE_KEY = 'tkr:labels_overrides';
const LEGACY_DEFAULT_LABEL_OVERRIDES: Array<{ path: string; values: string[] }> = [
  { path: 'phase3.qualityPoor', values: ['△'] },
];

// Deep merge: override only leaves that exist in `overrides`.
function deepMerge<T extends Record<string, unknown>>(
  base: T,
  overrides: Record<string, unknown>
): T {
  const result = { ...base } as Record<string, unknown>;
  for (const key of Object.keys(overrides)) {
    if (!(key in base)) continue;
    const baseVal = (base as Record<string, unknown>)[key];
    const overVal = overrides[key];
    if (
      baseVal &&
      overVal &&
      typeof baseVal === 'object' &&
      typeof overVal === 'object' &&
      !Array.isArray(baseVal)
    ) {
      result[key] = deepMerge(
        baseVal as Record<string, unknown>,
        overVal as Record<string, unknown>
      );
    } else {
      result[key] = overVal;
    }
  }
  return result as T;
}

// Set value in nested object by dot path (immutable).
function setByPath(
  obj: Record<string, unknown>,
  path: string,
  value: string
): Record<string, unknown> {
  const parts = path.split('.');
  if (parts.length === 1) {
    return { ...obj, [parts[0]]: value };
  }
  const [head, ...rest] = parts;
  const child =
    obj[head] && typeof obj[head] === 'object' ? { ...(obj[head] as Record<string, unknown>) } : {};
  return { ...obj, [head]: setByPath(child, rest.join('.'), value) };
}

// Remove a nested value by dot path and prune empty parent objects.
function deleteByPath(obj: Record<string, unknown>, path: string): Record<string, unknown> {
  const parts = path.split('.');
  if (parts.length === 0) return obj;

  const [head, ...rest] = parts;
  if (!(head in obj)) return obj;

  if (rest.length === 0) {
    const next = { ...obj };
    delete next[head];
    return next;
  }

  const child = obj[head];
  if (!child || typeof child !== 'object' || Array.isArray(child)) return obj;

  const nextChild = deleteByPath(child as Record<string, unknown>, rest.join('.'));
  const next = { ...obj };
  if (Object.keys(nextChild).length === 0) {
    delete next[head];
  } else {
    next[head] = nextChild;
  }
  return next;
}

// Get value from nested object by dot path.
function getByPath(obj: Record<string, unknown>, path: string): string | undefined {
  const parts = path.split('.');
  let current: unknown = obj;
  for (const part of parts) {
    if (current == null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return typeof current === 'string' ? current : undefined;
}

function sanitizeLegacyDefaultOverrides(overrides: Record<string, unknown>): {
  overrides: Record<string, unknown>;
  changed: boolean;
} {
  let next = overrides;
  let changed = false;
  const defaults = defaultLabels as unknown as Record<string, unknown>;

  for (const migration of LEGACY_DEFAULT_LABEL_OVERRIDES) {
    const currentValue = getByPath(next, migration.path);
    const currentDefault = getByPath(defaults, migration.path);
    if (
      currentValue &&
      currentDefault &&
      currentValue !== currentDefault &&
      migration.values.includes(currentValue)
    ) {
      next = deleteByPath(next, migration.path);
      changed = true;
    }
  }

  return { overrides: next, changed };
}

function persistOverridesToStorage(overrides: Record<string, unknown>) {
  if (Object.keys(overrides).length === 0) {
    localStorage.removeItem(LABELS_STORAGE_KEY);
    return;
  }
  localStorage.setItem(LABELS_STORAGE_KEY, JSON.stringify(overrides));
}

// Count modified labels (overrides that differ from defaults).
function countModified(
  overrides: Record<string, unknown>,
  defaults: Record<string, unknown>,
  prefix = ''
): number {
  let count = 0;
  for (const key of Object.keys(overrides)) {
    const fullPath = prefix ? `${prefix}.${key}` : key;
    const overVal = overrides[key];
    const defVal = defaults[key];
    if (
      overVal &&
      typeof overVal === 'object' &&
      !Array.isArray(overVal) &&
      defVal &&
      typeof defVal === 'object'
    ) {
      count += countModified(
        overVal as Record<string, unknown>,
        defVal as Record<string, unknown>,
        fullPath
      );
    } else if (typeof overVal === 'string') {
      const defaultStr = getByPath(
        defaults,
        fullPath
          .split('.')
          .slice(prefix ? prefix.split('.').length : 0)
          .join('.')
      );
      if (overVal !== defaultStr) count += 1;
    }
  }
  return count;
}

interface LabelsContextValue {
  labels: Labels;
  saveLabels: (overrides: Partial<Labels>) => Promise<void>;
  resetLabels: () => void;
  updateLabel: (path: string, value: string) => void;
  isEditMode: boolean;
  modifiedCount: number;
}

const LabelsContext = createContext<LabelsContextValue>({
  labels: defaultLabels,
  saveLabels: async () => {},
  resetLabels: () => {},
  updateLabel: () => {},
  isEditMode: false,
  modifiedCount: 0,
});

export const useLabels = () => useContext(LabelsContext);

export const useEditMode = () => {
  const { isEditMode } = useContext(LabelsContext);
  return isEditMode;
};

function loadOverridesFromStorage(): Record<string, unknown> {
  try {
    const raw = localStorage.getItem(LABELS_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        const { overrides, changed } = sanitizeLegacyDefaultOverrides(
          parsed as Record<string, unknown>
        );
        if (changed) persistOverridesToStorage(overrides);
        return overrides;
      }
    }
  } catch {
    // ignore
  }
  return {};
}

// Floating toolbar component for edit mode.
const EditModeToolbar: React.FC<{
  modifiedCount: number;
  onSave: () => void;
  onReset: () => void;
  saving: boolean;
}> = ({ modifiedCount, onSave, onReset, saving }) => {
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 99999,
        background: 'linear-gradient(90deg, #f59e0b, #d97706)',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 24px',
        fontSize: 14,
        fontWeight: 600,
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span
          style={{
            background: '#fff',
            color: '#d97706',
            borderRadius: 4,
            padding: '2px 8px',
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          EDIT
        </span>
        <span>
          {modifiedCount > 0
            ? `${modifiedCount} label${modifiedCount > 1 ? 's' : ''} modified`
            : 'Click any text to edit'}
        </span>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          onClick={onSave}
          disabled={saving}
          style={{
            padding: '4px 16px',
            background: '#fff',
            color: '#d97706',
            border: 'none',
            borderRadius: 4,
            cursor: saving ? 'not-allowed' : 'pointer',
            fontWeight: 600,
            fontSize: 13,
          }}
        >
          {saving ? '...' : '\u9069\u7528'}
        </button>
        <button
          onClick={onReset}
          style={{
            padding: '4px 16px',
            background: 'rgba(255,255,255,0.2)',
            color: '#fff',
            border: '1px solid rgba(255,255,255,0.5)',
            borderRadius: 4,
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: 13,
          }}
        >
          {'\u30EA\u30BB\u30C3\u30C8'}
        </button>
      </div>
    </div>
  );
};

export const LabelsProvider: React.FC<{ children: React.ReactNode; isAuthenticated?: boolean }> = ({
  children,
  isAuthenticated = false,
}) => {
  const [overrides, setOverrides] = useState<Record<string, unknown>>(() =>
    loadOverridesFromStorage()
  );
  const [saving, setSaving] = useState(false);

  const isEditMode = useMemo(() => {
    if (!isAuthenticated) return false;
    const params = new URLSearchParams(window.location.search);
    return params.get('edit') === '1';
  }, [isAuthenticated]);

  const labels = useMemo(
    () =>
      deepMerge(
        defaultLabels as unknown as Record<string, unknown>,
        overrides
      ) as unknown as Labels,
    [overrides]
  );

  const modifiedCount = useMemo(
    () => countModified(overrides, defaultLabels as unknown as Record<string, unknown>),
    [overrides]
  );

  // On mount, try to fetch labels from the backend (if available).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchWithAuth(`/api/settings/labels`);
        if (res.ok) {
          const data = await res.json();
          if (!cancelled && data && typeof data === 'object') {
            const remote = data as Record<string, unknown>;
            const { overrides: sanitizedRemote } = sanitizeLegacyDefaultOverrides(remote);
            // Only apply if it has content.
            if (Object.keys(remote).length > 0) {
              persistOverridesToStorage(sanitizedRemote);
              setOverrides(sanitizedRemote);
            }
          }
        }
      } catch {
        // Backend unavailable; use local overrides.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const saveLabels = useCallback(async (newOverrides: Partial<Labels>) => {
    const rawMerged = { ...loadOverridesFromStorage(), ...newOverrides } as Record<string, unknown>;
    const { overrides: merged } = sanitizeLegacyDefaultOverrides(rawMerged);
    persistOverridesToStorage(merged);
    setOverrides(merged);

    // Best-effort save to backend.
    try {
      await fetchWithAuth(`/api/settings/labels`, {
        method: 'PUT',
        body: JSON.stringify(merged),
      });
    } catch {
      // Ignore network errors; localStorage is the primary store.
    }
  }, []);

  const resetLabels = useCallback(() => {
    localStorage.removeItem(LABELS_STORAGE_KEY);
    setOverrides({});
    // Best-effort clear on backend.
    fetchWithAuth(`/api/settings/labels`, {
      method: 'PUT',
      body: JSON.stringify({}),
    }).catch(() => {});
  }, []);

  const updateLabel = useCallback((path: string, value: string) => {
    setOverrides((prev) => {
      const defaultVal = getByPath(defaultLabels as unknown as Record<string, unknown>, path);
      // If value matches default, remove the override for this path.
      if (value === defaultVal) {
        // For simplicity, just set it; deepMerge will handle it correctly.
        // A full "remove" would be complex; setting to default is equivalent.
        const next = setByPath(prev, path, value);
        localStorage.setItem(LABELS_STORAGE_KEY, JSON.stringify(next));
        return next;
      }
      const next = setByPath(prev, path, value);
      localStorage.setItem(LABELS_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const handleToolbarSave = useCallback(async () => {
    setSaving(true);
    try {
      await saveLabels(overrides as Partial<Labels>);
    } finally {
      setSaving(false);
    }
  }, [overrides, saveLabels]);

  return (
    <LabelsContext.Provider
      value={{ labels, saveLabels, resetLabels, updateLabel, isEditMode, modifiedCount }}
    >
      {isEditMode && (
        <EditModeToolbar
          modifiedCount={modifiedCount}
          onSave={handleToolbarSave}
          onReset={resetLabels}
          saving={saving}
        />
      )}
      {isEditMode && <div style={{ height: 40 }} />}
      {children}
    </LabelsContext.Provider>
  );
};

export default LabelsContext;
